import { Component, FormEvent } from 'react'
import { validate as uuidValidate } from 'uuid'
import { ApolloQueryResult } from '@apollo/client'
import { Form, Icon as LegacyIcon } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import {
  CloseOutlined,
  MenuOutlined,
  SearchOutlined,
  WifiOutlined,
} from '@ant-design/icons'
import { Input, Dropdown, Select, Tooltip, Switch, Cascader } from 'antd'
import { Flex, Card, Heading, Box } from '@weareberyl/design-system'
import { capitalCase } from 'change-case'

import Query from '~/components/Query'
import { withScheme } from '~/components/Scheme'
import { VehicleContextMenu } from '~/components/Module/context'
import {
  colorByOnlineStatus,
  colorByFixInvalid,
  iconByFixInvalid,
  colorByModuleState,
  capitalize,
  getIcon,
  isDeploymentDisabled,
} from '~/utils'
import { isPhone } from '~/utils/mobileDetect'
import Table, { ICustomColumnProps } from '~/components/Table'
import { QueryParams, withQueryParams } from '~/utils/useQueryParams'
import { VehicleLink } from '~/components/Link'
import { getVehicleTitle } from '~/components/ModuleHeader'
import StartDeployment from '~/components/Deployment/Start'
import HeadTitle from '~/components/HeadTitle'
import { MODULES_LIST_DETAILED_INFORMATION_FLAG } from '~/constants/localStorage'

import { SCHEME } from '~/types'
import { withFeatureFlags } from '~/utils/withFeatureFlags'
import { FeatureFlags } from '~/context/featureFlags'
import { LockState } from '~/__generated__/globalTypes'
import {
  FrameType,
  ModuleHardware,
  Vehicle,
  VehiclesListDocument,
  VehiclesListQuery,
} from '~/gql/generated/graphql'
import FrameTypeIcon from '~/components/ModuleHeader/FrameTypeIcon'
import AvailabilityIcon from '~/components/AvailabilityIcon'
import ServiceNowButton from '~/components/ServiceNow/ServiceNowButton'

const POSTGRES_INT_LIMIT = 2147483647

export const columns: (ICustomColumnProps<Vehicle> & {
  basic?: boolean
  zone?: boolean
})[] = [
  {
    title: 'Frame',
    render: vehicle => {
      return (
        <Flex style={{ flexWrap: 'nowrap' }}>
          <VehicleLink
            id={vehicle?.detail?.module?.id}
            bikeScheme={vehicle?.detail?.scheme}
          >
            {getVehicleTitle(
              vehicle?.frame_id ?? null,
              vehicle?.frame_type,
              vehicle?.detail?.module?.id,
            )}
          </VehicleLink>
          <FrameTypeIcon frameType={vehicle?.frame_type} mt="2px" ml={2} />
        </Flex>
      )
    },
    basic: true,
  },
  {
    title: 'Frame Type',
    className: 'xs-hide',
    dataIndex: ['frame_type'],
    render: frame_type => capitalCase(frame_type),
  },
  {
    title: 'Module ID',
    dataIndex: ['detail', 'module', 'id'],
    sorter: true,
    sortFunction: (direction: string) => ({ id: direction }),
    render: (module_id: string, vehicle) => (
      <VehicleLink
        id={module_id}
        bikeScheme={vehicle?.detail?.scheme ?? undefined}
      />
    ),
    basic: true,
  },
  {
    title: 'Unlock ID(s)',
    render: (vehicle: Vehicle) => {
      let unlock_ids = `${vehicle?.detail?.module?.unlock_id}`

      if (vehicle?.detail?.vehicle_unlock_id) {
        unlock_ids += ` / ${vehicle?.detail?.vehicle_unlock_id}`
      }

      return unlock_ids
    },
    basic: true,
  },
  {
    title: 'Drive Battery',
    className: 'xs-hide',
    dataIndex: ['detail', 'module', 'telemetry', 'drive', 'battery_percent'],
    render: battery => (battery ? `${battery}%` : '-'),
  },
  {
    title: 'Drive Range',
    className: 'xs-hide',
    dataIndex: ['detail', 'module', 'telemetry', 'drive', 'estimated_range'],
    render: range => (range ? `${range} km` : '-'),
    zone: true,
  },
  {
    title: 'Module Battery',
    className: 'xs-hide',
    dataIndex: ['detail', 'module', 'telemetry', 'battery_percent'],
    sorter: true,
    key: 'battery_percent',
    sortFunction: direction => ({
      telemetry: {
        battery_percent: direction,
      },
    }),
    render: battery => (battery ? `${battery}%` : '-'),
  },
  {
    title: 'Power mode',
    className: 'xs-hide',
    dataIndex: ['detail', 'module', 'iot_state', 'power_mode'],
  },
  {
    title: 'Firmware',
    className: 'xs-hide',
    dataIndex: ['detail', 'module', 'iot_state', 'firmware_version'],
  },
  {
    title: 'Available',
    className: 'xs-hide',
    dataIndex: ['detail', 'module', 'availability', 'status'],
    align: 'center',
    key: 'available',
    render: m => (
      <span>
        <AvailabilityIcon status={m} />
      </span>
    ),
    zone: true,
  },
  {
    title: 'GNSS fix',
    className: 'xs-hide',
    dataIndex: ['detail', 'module', 'telemetry', 'fix_invalid'],
    align: 'center',
    key: 'fix_invalid',
    render: fix_invalid =>
      fix_invalid != null ? (
        <span>
          <LegacyIcon
            type={iconByFixInvalid(fix_invalid)}
            style={{
              fontSize: 22,
              color: colorByFixInvalid(fix_invalid),
            }}
          />
        </span>
      ) : (
        <span />
      ),
  },
  {
    title: 'Connectivity',
    dataIndex: ['detail', 'module', 'connectivity', 'is_online'],
    align: 'center',
    render: isOnline =>
      isOnline != null ? (
        <span>
          <WifiOutlined
            style={{ fontSize: 22, color: colorByOnlineStatus(isOnline) }}
          />
        </span>
      ) : (
        <span />
      ),
  },
]

export const SearchText = ({ text }: { text: string }) => (
  <div style={{ marginBottom: 12 }}>Search text: "{text}"</div>
)

export const getSearchString = (filterType: string, searchValue: string) => {
  switch (filterType) {
    case 'contains':
      return `%${searchValue}%`
    case 'matches':
      return `${searchValue}`
    case 'starts_with':
      return `${searchValue}%`
    case 'ends_with':
      return `%${searchValue}`
    default:
      return ''
  }
}

const createFilter = (
  filterField: string,
  filterType: string,
  searchValue: string,
  frameType: FrameType | string,
) => {
  const filterVars = {
    [`${filterField}__like`]: getSearchString(filterType, searchValue),
  }

  if (frameType) {
    filterVars['frame_type'] = frameType
  }

  return filterVars
}

const VehicleListTable = ({ tableProps, scheme, showDetailedInfo }) => {
  const lockStateColumn: ICustomColumnProps<Vehicle> = {
    title: 'Lock State',
    // TODO: need to move this to vehicle_lock_state
    dataIndex: ['detail', 'module', 'lock_state', 'overall_state'],
    sorter: true,
    sortFunction: direction => ({
      lock_state: {
        overall_state: direction,
      },
    }),
    align: 'center',
    render: (m: LockState) => {
      if (!m) {
        return null
      }
      const stateText = capitalize(m)
      return (
        <Tooltip title={stateText ?? ''}>
          <LegacyIcon
            type={getIcon(m).icon}
            style={{ fontSize: 16, color: colorByModuleState(m) }}
          />
        </Tooltip>
      )
    },
  }
  const actionColumn: ICustomColumnProps<Vehicle> = {
    title: '',
    className: 'xs-hide',
    align: 'center',
    render: ({ id, detail }: Vehicle) => (
      <Dropdown
        overlay={
          <VehicleContextMenu
            module_id={detail?.module?.id as string}
            hardware_type={detail?.module?.hardware_type as ModuleHardware}
            vehicleId={id}
            lock_configuration={detail?.lock_configuration}
          />
        }
      >
        <MenuOutlined />
      </Dropdown>
    ),
  }
  return (
    <Table
      id="vehicles-table"
      columns={[
        ...columns.filter(({ basic }) => showDetailedInfo || basic),
        ...(showDetailedInfo ? [lockStateColumn] : []),
        actionColumn,
      ]}
      scheme={scheme}
      {...tableProps}
    />
  )
}

type Props = {
  scheme: SCHEME
  queryParams: typeof defaultQueryParams & QueryParams
  setQueryParams: (params: QueryParams) => void
  featureFlags: FeatureFlags
}

type State = {
  unlockIdValue: string
  selection: Vehicle[]
  version: string
  showDetailedInfo: boolean
}

const defaultState: State = {
  unlockIdValue: '',
  selection: [],
  version: '',
  showDetailedInfo: true,
}

const defaultQueryParams = {
  filterField: 'frame_id',
  filterType: 'contains',
  searchValue: '',
  frameType: '',
}

interface IFilterType {
  onChange: (filter: [string, string]) => void
  defaultValue: [string, string]
  module?: boolean
}

export const FilterType = ({ onChange, defaultValue, module }: IFilterType) => {
  const fieldOptions = module
    ? ['module_id', 'unlock_id']
    : ['frame_id', 'unlock_id', 'module_id', 'vehicle_id']
  const typeOptions = ['contains', 'matches', 'starts_with', 'ends_with']
  const options = fieldOptions.map(field => ({
    value: field,
    label: capitalCase(field),
    children: typeOptions.map(type => ({
      value: type,
      label: capitalCase(type),
    })),
  }))
  return (
    <Cascader
      expandTrigger="hover"
      options={options}
      onChange={onChange}
      defaultValue={defaultValue}
      allowClear={false}
      data-testid="filter-select"
    />
  )
}

class VehicleList extends Component<Props, State> {
  state = defaultState

  componentDidMount() {
    this._rehydrateDetailedInfoSetting()
  }

  _onFilterChange = (filter: [string, string]) => {
    const [filterField, filterType] = filter
    if (this._isInputValid(filterField, filterType, this.state.unlockIdValue)) {
      this.props.setQueryParams({ filterField, filterType })
    } else {
      this.props.setQueryParams({ searchValue: '', filterField, filterType })
      this.setState({ unlockIdValue: '' })
    }
  }

  _onFrameTypeChange = (frameType: FrameType) => {
    this.props.setQueryParams({ frameType })
  }

  _onSubmit = (e: FormEvent) => {
    e.preventDefault()

    const { unlockIdValue } = this.state
    const { filterField, filterType } = this.props.queryParams
    if (this._isInputValid(filterField, filterType, unlockIdValue)) {
      this.props.setQueryParams({ searchValue: unlockIdValue, current: 1 })
    }
  }

  _onChangeSelection = (_selectedRowKeys, selectedRows) => {
    // tslint:disable
    this.setState({ selection: selectedRows })
  }

  //skipcq JS-0105
  _getCheckboxProps = record => {
    return {
      disabled: isDeploymentDisabled(record?.detail?.module?.hardware_type),
    }
  }

  _renderCloseIcon = () => {
    return <CloseOutlined onClick={() => this.setState(defaultState)} />
  }

  //skipcq JS-0105
  _isInputValid = (
    filterField: string,
    filterType: string,
    input: string,
  ): boolean => {
    if (input === '') {
      return true
    }

    if (filterField === 'frame_id') {
      if (isNaN(Number(input))) {
        return false
      } else {
        return parseInt(input) < POSTGRES_INT_LIMIT
      }
    }

    if (filterField === 'vehicle_id' && filterType === 'matches') {
      if (!uuidValidate(input)) {
        return false
      }
    }

    return true
  }

  _rehydrateDetailedInfoSetting = () => {
    const showDetailedInfo = window.localStorage.getItem(
      MODULES_LIST_DETAILED_INFORMATION_FLAG,
    )
    if (showDetailedInfo) {
      this.setState({
        showDetailedInfo: showDetailedInfo === 'true',
      })
    }
  }

  _toggleDetailedInfo = () => {
    this.setState(
      ({ showDetailedInfo }) => ({
        showDetailedInfo: !showDetailedInfo,
      }),
      () => {
        window.localStorage.setItem(
          MODULES_LIST_DETAILED_INFORMATION_FLAG,
          String(this.state.showDetailedInfo),
        )
      },
    )
  }

  _renderLayerToggle() {
    const { showDetailedInfo } = this.state

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '8px',
        }}
      >
        <p style={{ marginBottom: 0, marginRight: 20 }}>Detailed information</p>
        <Switch
          checked={showDetailedInfo}
          onChange={this._toggleDetailedInfo}
        />
      </div>
    )
  }

  static _getValidationMessage(filterField: string) {
    if (filterField === 'frame_id') {
      return 'Frame Id can only contain numbers, and cannot exceed 10 digits.'
    } else if (filterField === 'vehicle_id') {
      return 'Vehicle Id must be a valid uuid.'
    } else {
      return ''
    }
  }

  render() {
    const { scheme } = this.props
    const { unlockIdValue, showDetailedInfo, selection } = this.state

    const { searchValue, filterField, filterType, frameType } =
      this.props.queryParams

    // If a scheme is specified then query users for this scheme only.
    // If not, query all users as we're in Beryl Admin mode
    const scheme_id = scheme ? scheme.id : null
    const isValid = this._isInputValid(filterField, filterType, unlockIdValue)

    const frameTypesOptions = [
      {
        value: '',
        label: 'All',
      },
    ]

    Object.entries(FrameType).forEach(([_, value]) => {
      frameTypesOptions.push({
        value,
        label: capitalCase(value),
      })
    })

    return (
      <>
        <HeadTitle pageTitle="Vehicles" />
        <Card p={5}>
          <Flex justifyContent="space-between" alignItems="flex-end" mb={5}>
            <Heading variant="callout">Vehicles</Heading>
            <Box flexDirection="row" style={{ gap: 5 }}>
              <StartDeployment
                module_ids={selection.map(d => d?.detail?.module?.id as string)}
              />
              <ServiceNowButton schemeId={scheme?.id} fleetType="vehicle" />
            </Box>
          </Flex>
          <Flex style={{ gap: '10px', display: 'flex' }}>
            <Form
              onSubmit={this._onSubmit}
              style={{ flex: 1, display: 'flex' }}
            >
              <Form.Item
                validateStatus={!isValid ? 'error' : undefined}
                style={{ flex: '0 1 70%', marginRight: '20px' }}
                help={
                  !isValid
                    ? VehicleList._getValidationMessage(filterField)
                    : undefined
                }
              >
                <Input
                  id="modules-search-input"
                  placeholder="Search"
                  addonBefore={
                    <FilterType
                      onChange={this._onFilterChange}
                      defaultValue={[filterField, filterType]}
                    />
                  }
                  onChange={e => {
                    this.setState({
                      unlockIdValue: e.currentTarget.value,
                    })
                  }}
                  defaultValue={searchValue}
                  prefix={<SearchOutlined />}
                  suffix={unlockIdValue && this._renderCloseIcon()}
                />
              </Form.Item>
              <Form.Item
                label="Frame type"
                style={{
                  flex: '0 0 30%',
                  margin: '0 10px 0 0',
                  display: 'flex',
                }}
              >
                <Select
                  defaultValue={frameType}
                  style={{ width: '100%' }}
                  onChange={this._onFrameTypeChange}
                  options={frameTypesOptions}
                />
              </Form.Item>
              <input className="hide" type="submit" />
            </Form>

            {this._renderLayerToggle()}
          </Flex>
          {searchValue && <SearchText text={searchValue} />}
          <Query
            query={VehiclesListDocument}
            variables={{
              scheme_id,
              is_mobile: isPhone() || !showDetailedInfo,
              lean_query: !showDetailedInfo,
              paginate: {
                page: this.props.queryParams.current,
                per_page: this.props.queryParams.pageSize,
              },
              ...createFilter(filterField, filterType, searchValue, frameType),
            }}
            waitFor="data.table"
            opts={{ showRefetchLoader: true }}
          >
            {(props: ApolloQueryResult<VehiclesListQuery>) => (
              <VehicleListTable
                tableProps={{
                  ...props,
                  onChange: ({ current, pageSize }) => {
                    this.props.setQueryParams({ current, pageSize })
                  },
                  rowSelection: {
                    onChange: this._onChangeSelection,
                    getCheckboxProps: this._getCheckboxProps,
                  },
                }}
                scheme={scheme}
                showDetailedInfo={showDetailedInfo}
              />
            )}
          </Query>
        </Card>
      </>
    )
  }
}

export default withFeatureFlags(
  withScheme(withQueryParams('module-table', VehicleList, defaultQueryParams)),
)
